import { useMemo, useRef, useCallback } from "react";
import { experimental_useObject as useObject } from "@ai-sdk/react";
import { z } from "zod";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";

interface UseStreamObjectOptions<T> {
  api: string;
  schema: z.Schema<T>;
  label: string;
  onFinish?: (object: T | undefined, error: Error | undefined) => void | Promise<void>;
  abortRef?: React.MutableRefObject<AbortController | null>;
}

export function useStreamObject<T>(opts: UseStreamObjectOptions<T>) {
  const { access_token } = useAuth();
  const { toast } = useToast();

  const headers = useMemo(() => {
    const h: Record<string, string> = {};
    if (access_token) {
      h["Authorization"] = `Bearer ${access_token}`;
    }
    return h;
  }, [access_token]);

  const labelRef = useRef(opts.label);

  const customFetch = useCallback(
    async (url: string, options?: RequestInit) => {
      const fetchOptions: RequestInit = { ...options };
      if (opts.abortRef) {
        opts.abortRef.current = new AbortController();
        fetchOptions.signal = opts.abortRef.current.signal;
      }

      return fetch(url, fetchOptions);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return useObject({
    api: opts.api,
    schema: opts.schema,
    headers,
    fetch: customFetch,
    onFinish: async ({ object, error }) => {
      if (error) {
        const isTypeValidationError =
          error.message?.includes("Type validation failed") ||
          error.name === "AI_TypeValidationError";
        toast({
          title: "Error",
          description: isTypeValidationError
            ? `Failed to generate ${labelRef.current}. The AI response was incomplete. Please try again.`
            : error.message || `Failed to generate ${labelRef.current}. Please try again.`,
          variant: "destructive",
        });
      }
      await opts.onFinish?.(object as T | undefined, error);
    },
    onError: (err: Error) => {
      if (err.name === "AbortError" || err.message === "Aborted") {
        return;
      }
      toast({
        title: "Error",
        description: err.message || `Failed to generate ${labelRef.current}. Please try again.`,
        variant: "destructive",
      });
    },
  });
}